import React from 'react';
import './ServicesPreview.css';

/**
 * Customer-facing preview of a salon's service listing:
 * Category → Service → Sub Services, as the mobile app will show it.
 * Pure presentational — pass the output of buildServiceCategoriesFromRows().
 */
const hasPrice = (item) => Number(item.price) > 0;

const PriceTag = ({ item }) =>
  hasPrice(item) ? (
    <span className="preview-price">₹{item.price}</span>
  ) : (
    <span className="preview-price preview-price-missing" title="Price is missing — publishing is blocked until it is set">
      ⚠ No price
    </span>
  );

function ServicesPreview({ serviceCategories, combos }) {
  const hasCombos = combos && combos.length > 0;
  if ((!serviceCategories || serviceCategories.length === 0) && !hasCombos) {
    return (
      <div className="services-preview-empty">
        <p>Nothing to preview yet</p>
        <small>Add services in the entry grid to see how customers will see them</small>
      </div>
    );
  }

  const missingPriceCount =
    (serviceCategories || [])
      .flatMap((c) => c.services || [])
      .flatMap((s) => (s.subServices?.length ? s.subServices : [s]))
      .filter((item) => !hasPrice(item)).length +
    (combos || []).filter((c) => !(Number(c.totalPrice) > 0)).length;

  return (
    <div className="services-preview">
      {missingPriceCount > 0 && (
        <div className="preview-price-warning">
          ⚠ {missingPriceCount} item{missingPriceCount === 1 ? ' has' : 's have'} no price (highlighted
          in red below). Publishing is blocked until every service has a price.
        </div>
      )}
      {(serviceCategories || []).map((category) => (
        <div key={category.name} className="preview-category">
          <h3 className="preview-category-name">{category.name}</h3>
          <div className="preview-services">
            {(category.services || []).map((service, index) => {
              const cardMissingPrice = service.subServices?.length
                ? service.subServices.some((sub) => !hasPrice(sub))
                : !hasPrice(service);
              return (
              <div
                key={`${service.name}-${service.genderType}-${index}`}
                className={`preview-service-card${cardMissingPrice ? ' preview-card-warning' : ''}`}
              >
                <div className="preview-service-header">
                  <span className="preview-service-name">{service.name}</span>
                  {service.genderType && (
                    <span className={`preview-gender-badge gender-${(service.genderType || '').toLowerCase()}`}>
                      {service.genderType}
                    </span>
                  )}
                  {!service.subServices && (
                    <span className="preview-service-right">
                      <span className="preview-duration">⏱ {service.duration} mins</span>
                      <PriceTag item={service} />
                    </span>
                  )}
                </div>
                {service.subServices && service.subServices.length > 0 && (
                  <div className="preview-subservices">
                    {service.subServices.map((sub, subIndex) => (
                      <div key={`${sub.name}-${subIndex}`} className="preview-subservice-row">
                        <span className="preview-subservice-name">{sub.name}</span>
                        <span className="preview-service-right">
                          <span className="preview-duration">⏱ {sub.duration} mins</span>
                          <PriceTag item={sub} />
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              );
            })}
          </div>
        </div>
      ))}

      {hasCombos && (
        <div className="preview-category">
          <h3 className="preview-category-name">✨ Combo Offers</h3>
          <div className="preview-services">
            {combos.map((combo, index) => (
              <div
                key={`${combo.title}-${index}`}
                className={`preview-service-card preview-combo-card${
                  !(Number(combo.totalPrice) > 0) ? ' preview-card-warning' : ''
                }`}
              >
                <div className="preview-service-header">
                  <span className="preview-service-name">{combo.title}</span>
                  <span className="preview-service-right">
                    <PriceTag item={{ price: combo.totalPrice }} />
                  </span>
                </div>
                <div className="preview-combo-includes">
                  {(combo.services || []).map((s) => s.name).join(' + ')}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default ServicesPreview;
